import type { FrequencyCap } from './frequency-cap.ts'
import type { GlobalPromptReferrerConditions } from './global-prompt-referrer-conditions.ts'
import type { ECommItemType } from '../../../sdk/src/enums/e-comm-item-type.ts'
import { testSafeCaseInsensitivePattern } from '../../../sdk/src/utils/safe-dynamic-pattern.ts'
import { Globals } from '../globals.ts'
import { Env } from '../env.ts'
import { SessionConfig } from './session-config.ts'

/** @public */
type DomainFrequencyCaps = {
    readonly prompts?: FrequencyCap
    readonly app_messages?: FrequencyCap
}

/** @public */
export type DomainGlobalPromptSettings = {
    readonly referrers?: GlobalPromptReferrerConditions
}

/** @public */
export type DomainECommConfig = {
    readonly item_type: ECommItemType
}

/** @public */
export type DomainConfig = {
    readonly id: number
    readonly name: string
    readonly domain_key: string
    readonly time_zone: string

    readonly frequency_caps?: DomainFrequencyCaps
    readonly global_prompt_settings: DomainGlobalPromptSettings

    readonly flags: string[]

    readonly default_icon_url?: string

    readonly sdk_injected_scripts?: string[]

    readonly vapid_public_key: string

    readonly additional_subscription_data: unknown
    readonly whitelist_domains: string[]
    readonly sdk_event_only_domains: string[]
    readonly domain_integrations: unknown

    readonly ecomm_config: DomainECommConfig | null
    readonly session: SessionConfig | null
}

// region Internal Utilities

/** @internal */
export function isForceDebugLogsFlagEnabled(domain: DomainConfig): boolean {
    return domain.flags.includes(Globals._sdkHasForceDebugLogsEnabledFlag)
}

/** @internal **/
export function isEventOnlyMode(domain: DomainConfig): boolean {
    const allowedDomains = domain.whitelist_domains ?? []
    const eventOnlyDomains = domain.sdk_event_only_domains ?? []

    const hasEventOnlyFeatFlag = domain.flags.includes(Globals._sdkHasEventsOnlyDomainsFlag)
    const loc = Env._location
    const whiteListMatchFound = allowedDomains.some((entry) => matchesDomainEntry(entry, loc))
    const eventOnlyMatchFound = eventOnlyDomains.some((entry) => matchesDomainEntry(entry, loc))

    return hasEventOnlyFeatFlag && whiteListMatchFound && eventOnlyMatchFound
}

/**
 * Splits an entry on top-level `|`. Entries were historically compiled as regular expressions, so
 * alternation in a stored entry means what it looks like; matching it as a literal leaves the
 * entry permanently unmatched, which disables the SDK outright for a `whitelist_domains` row.
 */
function splitTopLevelAlternatives(entry: string): string[] {
    const alternatives: string[] = []
    let depth = 0
    let buffer = ''

    for (let i = 0; i < entry.length; i++) {
        const character = entry[i]

        if (character === '\\') {
            buffer += character + (entry[i + 1] ?? '')
            i += 1
            continue
        }

        if (character === '(') {
            depth += 1
        } else if (character === ')') {
            depth = Math.max(0, depth - 1)
        } else if (character === '|' && depth === 0) {
            alternatives.push(buffer)
            buffer = ''
            continue
        }

        buffer += character
    }

    alternatives.push(buffer)
    return alternatives.filter((alternative) => alternative.length > 0)
}

/**
 * Whether a trailing wildcard in `base` still sits inside the host portion of the entry.
 *
 * The host-boundary protection only matters while the wildcard could still extend a hostname.
 * Once the entry has reached a path, port, query or fragment, the wildcard is past the host and
 * consuming freely is exactly what the entry asks for - `customer.com/*` means everything under
 * that path, and bounding it there would take the entry dark.
 */
function wildcardIsInsideHost(base: string): boolean {
    return !/[/:?#]/.test(base.replace(/^https?:\/\//i, ''))
}

/**
 * Drops a trailing wildcard that is still inside the host, leaving the literal it qualifies.
 *
 * The candidate list already includes the bare host, so an entry like `pushly.com*` still matches
 * `pushly.com` on any path or port through that candidate - while `pushly.com.attacker.net` no
 * longer matches anything, which is the over-match this exists to close.
 *
 * `.` followed by `*` tokenizes as a single wildcard, so a form ending `.*` gives up two
 * characters rather than one; taking only one leaves a literal trailing dot no host carries, and
 * the entry then matches nothing at all - including its own host.
 */
function boundTrailingWildcard(form: string): string {
    if (!form.endsWith('*') || form.endsWith('\\*')) {
        return form
    }

    const base = form.slice(0, form.endsWith('.*') ? -2 : -1)

    // A bare `*` is allow-all, which is what the pre-rewrite regex did with it: a leading `*` was
    // rewritten to `.*` and anchored, matching every candidate. Left as a wildcard deliberately.
    if (base.length === 0) {
        return form
    }

    return wildcardIsInsideHost(base) ? base : form
}

/**
 * Expands one entry into the set of patterns that together carry its intended meaning.
 *
 * A leading `*.` also matches the apex: a domain configured `*.customer.com` and served on
 * `customer.com` is the same customer, and treating it as a miss disables the SDK there or flips
 * it out of event-only mode.
 */
function expandDomainEntry(entry: string): string[] {
    const forms = entry.startsWith('*.') ? [entry, entry.slice(2)] : [entry]

    return forms.map(boundTrailingWildcard)
}

export function matchesDomainEntry(domainStr: string, loc: Location): boolean {
    const currentHost = `${loc.protocol}//${loc.hostname}`
    const currentHostWithPort = `${loc.protocol}//${loc.host}`
    const currentHref = loc.href

    /**
     * Entries arrive from the domain payload, so the pattern is evaluated by the backtracking-free
     * matcher rather than compiled into a RegExp - a pathological entry compiled here blocks the
     * main thread for the life of the page. Scheme-stripped candidates stand in for the optional
     * scheme prefix an entry is allowed to omit.
     */
    const candidateValues = [
        currentHost,
        currentHostWithPort,
        currentHref,
        currentHost.replace(/^https?:\/\//i, ''),
        currentHostWithPort.replace(/^https?:\/\//i, ''),
        currentHref.replace(/^https?:\/\//i, ''),
    ]

    return splitTopLevelAlternatives(domainStr)
        .flatMap(expandDomainEntry)
        .some((pattern) =>
            candidateValues.some((candidateValue) =>
                testSafeCaseInsensitivePattern(candidateValue, pattern, { anchored: true }),
            ),
        )
}

// endregion
